import { Navigate, Link } from "react-router-dom";
import { ArrowRight, Check, Globe, Clock, Heart, Video, MessageCircle } from "lucide-react";
import { COUNTRIES } from "@/lib/countries";
import { CONTACT, IMG } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import useSeo from "@/lib/useSeo";
import { SafeHtml } from "@/lib/safeHtml";

const CountryLanding = ({ slug }) => {
  const data = COUNTRIES[slug];

  useSeo({
    title: data?.seoTitle,
    description: data?.seoDescription,
    path: `/yoga-oncologico-${slug}`,
  });

  if (!data) return <Navigate to="/sesiones-online" replace />;

  return (
    <div className="bg-ivory" data-testid={`country-page-${slug}`}>
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">
              {data.flag} &nbsp; {data.heroEyebrow}
            </p>
            <SafeHtml
              as="h1"
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05]"
              html={data.heroTitle.replace("<em>", "<em class=\"italic text-terracotta\">")}
            />
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
              {data.heroBody}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/sesion-gratuita"
                data-testid={`country-${slug}-cta-primary`}
                className="inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-7 py-4 text-sm font-medium hover:bg-terracotta transition-all"
              >
                Reserva sesión gratuita 15 min <ArrowRight size={15} />
              </Link>
              <a
                href={CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`country-${slug}-cta-whatsapp`}
                className="inline-flex items-center justify-center gap-2 border border-charcoal/30 text-charcoal rounded-full px-7 py-4 text-sm font-medium hover:bg-charcoal hover:text-ivory transition-all"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
              <img
                src={IMG.onlineYogaSession}
                alt={`Sesiones online de yoga oncológico para ${data.country}`}
                className="w-full h-full object-cover"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY ONLINE FROM SPAIN WORKS */}
      <section className="py-20 md:py-28 bg-sand/30 grain">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">¿Por qué online desde España?</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
              Distancia geográfica, <span className="italic">cercanía real</span>.
            </h2>
            <p className="mt-7 text-charcoal/70 leading-relaxed text-[16px]">
              En {data.country} todavía hay muy poca formación específica en acompañamiento
              cuerpo-mente para procesos oncológicos. Eso deja a muchas mujeres sin
              recursos especializados a su alcance. Las sesiones online resuelven exactamente
              esa brecha: <strong>la misma profundidad que una sesión presencial, sin barreras de ubicación</strong>.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Globe, t: "En todo " + data.country, d: data.cities + ". Solo necesitas conexión a internet." },
              { icon: Clock, t: "Tu zona horaria", d: data.timezone + ". Sin necesidad de adaptarte tú." },
              { icon: Video, t: "Por videollamada privada", d: "Una hora completa, espacio íntimo solo para ti, en español." },
              { icon: Heart, t: "Sin desplazamientos", d: "Ideal cuando hay fatiga, tratamiento activo o movilidad reducida." },
              { icon: Check, t: "Sin contagios", d: "Importante en tratamientos que bajan defensas (quimio, radio)." },
              { icon: Check, t: "Material guiado entre sesiones", d: "Notas y audios cortos para sostener el trabajo durante la semana." },
            ].map((f, i) => (
              <Reveal key={f.t} delay={i * 0.06}>
                <article className="bg-ivory rounded-3xl p-7 border border-sand/60 h-full">
                  <span className="w-11 h-11 rounded-full bg-sand flex items-center justify-center text-olive">
                    <f.icon size={18} />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-charcoal">{f.t}</h3>
                  <p className="mt-2 text-charcoal/65 leading-relaxed text-[14px]">{f.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOR WHO */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">Para mujeres en {data.country}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
              Acompañamiento <span className="italic">específico</span> según tu momento.
            </h2>
            <p className="mt-6 text-charcoal/70 leading-relaxed text-[16px]">
              No hay un protocolo cerrado. Cada sesión se diseña sobre tu realidad concreta:
              dónde estás del proceso, cómo te encuentras hoy, qué necesita tu cuerpo y tu sistema nervioso ahora mismo.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <ul className="space-y-4">
              {[
                "Mujeres con un diagnóstico oncológico reciente",
                "Pacientes en tratamiento activo (quimio, radio, hormonoterapia)",
                "Mujeres en fase de secuelas y recuperación post-tratamiento",
                "Personas con fibromialgia, dolor crónico o fatiga sostenida",
                "Mujeres con ansiedad o insomnio derivados de un proceso de salud",
                "Quien tiene movilidad reducida y no puede desplazarse a un centro",
              ].map((item) => (
                <li key={item} className="flex items-start gap-4 py-4 border-b border-sand">
                  <span className="w-8 h-8 rounded-full border border-terracotta/40 flex items-center justify-center text-terracotta flex-shrink-0">
                    <Check size={14} />
                  </span>
                  <span className="text-[15.5px] text-charcoal/85 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 md:py-28 bg-charcoal text-ivory grain">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-6">Sobre Marta</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
              Lo viví antes. Por eso lo entiendo <span className="italic text-terracotta">desde dentro</span>.
            </h2>
            <p className="mt-8 text-ivory/75 leading-relaxed text-[16.5px] max-w-2xl mx-auto">
              Soy Marta Montardit. Atravesé mi propio proceso oncológico mientras me formaba como
              profesora de yoga. De esa experiencia nació Método TAO. Hoy acompaño cada semana a
              mujeres en España, México, Argentina, Colombia, Chile y otros países hispanohablantes.
              He aparecido en La Vanguardia, RAC1 y Primera Persona TV.
            </p>
            <Link
              to="/mi-historia"
              data-testid={`country-${slug}-mi-historia`}
              className="mt-10 inline-flex items-center gap-2 text-sm text-ivory border-b border-terracotta pb-1 hover:text-terracotta transition-colors"
            >
              Lee mi historia <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      <SectionCTA
        title={`Da el primer paso, desde ${data.country}.`}
        body="Una primera videollamada gratuita de 15 minutos. Sin tarjeta, sin compromiso. Solo escucha real."
        primaryLabel="Reserva tu sesión gratuita"
        primaryTo="/sesion-gratuita"
      />
    </div>
  );
};

export default CountryLanding;
